"use client";

import React from "react";
import { MapPin, Phone, Mail, Building2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurHeadquarters() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const scrollToQuote = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById("start-your-project");
    if (target) {
      target.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  return <section data-section-id="224943" id="our-service-area" className="bg-[#F7F5F1] py-16 sm:py-24 lg:py-[110px] overflow-hidden text-foreground" ref={ref}>
      <div className={`max-w-[1280px] mx-auto px-6 lg:px-12 transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {/* Eyebrow + Heading pattern matching WhyChooseFirststop */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            WHERE WE OPERATE
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground mb-4">
            Roofing Services Across Southampton &amp; Hampshire
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto font-default leading-relaxed">
            Firststop Roofing Ltd proudly delivers professional roofing services in Southampton, Hampshire &amp; surrounding areas, providing local homeowners with dependable roofs and dedicated roofing care.
          </p>
        </div>

        {/* Two-column responsive card layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Company details card */}
          <div className="lg:col-span-5 flex flex-col justify-between p-8 sm:p-10 bg-card rounded-[14px] border border-border shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)]">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-primary" strokeWidth={1.75} />
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Local Specialists
                </span>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold font-default text-secondary-foreground mb-3">
                Firststop Roofing Ltd
              </h3>

              <p className="text-muted-foreground text-base font-default leading-relaxed mb-6">
                Professional roofing services for residential properties. Based locally, Matt and the team provide fast turnarounds, reliable site visits, and transparent quotes.
              </p>

              <div className="space-y-4 pt-6 border-t border-border">
                <div className="flex items-start gap-3.5">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <MapPin className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      Main Service Hub
                    </span>
                    <p className="text-base font-semibold text-secondary-foreground">
                      Serving Southampton, Hampshire &amp; surrounding areas
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <Phone className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      Direct Telephone
                    </span>
                    <a href="tel:07443802652" className="text-base font-semibold text-secondary-foreground hover:text-primary transition-colors inline-block">
                      07443 802652
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <Mail className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      Email Enquiry
                    </span>
                    <a href="mailto:Firststoproofinglimited@gmail.com" className="text-sm sm:text-base font-semibold text-secondary-foreground hover:text-primary transition-colors break-all inline-block">
                      Firststoproofinglimited@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border space-y-2.5">
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>Prompt local site inspections &amp; consultations</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>No pushy sales, honest, practical recommendations</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>Complete clean-up after every job</span>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <Button onClick={scrollToQuote} size="lg" className="w-full min-h-[48px] rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-semibold tracking-wide shadow-sm transition-all">
                Request a Quote
              </Button>
            </div>
          </div>

          {/* Map panel card */}
          <div className="lg:col-span-7 relative min-h-[360px] lg:min-h-full rounded-[14px] overflow-hidden border border-border shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)] bg-card">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662274/img/uk_service_map.png" alt="Service area map for Southampton, Hampshire and surrounding areas" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662274/img/uk_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662274/img/uk_service_map.png-wvc-sizes" className="absolute inset-0 w-full h-full object-cover" />
            {/* Soft gradient and map pin badge */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#15191D]/80 via-[#15191D]/30 to-transparent" />

            <div className="absolute bottom-6 left-6 right-6 p-5 sm:p-6 rounded-[14px] bg-[#15191D]/90 backdrop-blur-md border border-white/10 text-white">
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
                  <MapPin className="w-4 h-4" strokeWidth={2.5} />
                </span>
                <span className="text-sm font-bold uppercase tracking-wider text-primary">
                  Covering your local community
                </span>
              </div>
              <p className="text-white text-base font-semibold leading-snug">
                Southampton, Eastleigh, Chandler&apos;s Ford, Totton, Hedge End, Romsey, Fareham, Winchester, Hamble and all nearby Hampshire towns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}